import { bookingInitialValue } from './bookingInitialValue';
import { TRANSFER, BACK_TRANSFER } from '../../models/transfer';
import { getIn } from '../../../common/utils/getIn';
import { isTransferLate } from '../../../common/utils/is-transfer-late';
import { SERVICES } from '../../../constants';

const BOOKING_ACTION = {
    UPDATE: 'update',
    UPDATE_USER: 'update_user',
    ADD_TRANSFER: 'add_transfer',
    REMOVE_TRANSFER: 'remove_transfer',
    RESET: 'reset',
    TOUCH: 'touch',
    SHOW_ERRORS: 'show_errors',
    // time passed (interval, clock sync, submit guard): re-evaluate the time-gated extras only
    TICK: 'tick',
};

const notBookingDataChanges = ['touch', 'price', 'busy'];

function touch(previous, keyArray, value) {
    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((obj, key) => touch(obj, [...keyArray, key], value[key]), previous);
    }
    return previous.setIn(['touched', ...keyArray], true);
}

const bookingReducer = (previous, action) => {
    if (action.type === BOOKING_ACTION.RESET) {
        return bookingInitialValue;
    }

    if (previous.busy && action.name !== 'busy') {
        return previous;
    }

    switch (action.type) {
        case BOOKING_ACTION.UPDATE:
            const path = action.name.split('.');
            if (path[0] === 'transfers') {
                previous = transfersRelated({ previous, path, action });
            }
            previous = previous.setIn(path, action.value);

            // Selecting a company takes the booking outside the loyalty program (SoT §6 —
            // corporate bookings neither earn nor redeem), so drop the redemption opt-in the
            // same way an identity change does in UPDATE_USER below. Without this, a box
            // ticked before the company was picked would stay true in state and still be sent
            // as redeemLoyaltyCredit while the checkbox is already hidden. Read back from the
            // updated state so it covers both `company` (whole object) and `company.*` writes.
            if (path[0] === 'company' && previous.getIn(['company', 'id'])) {
                previous = previous.setIn(['redeemLoyaltyCredit'], false);
            }

            // Re-clamp per-leg legroom after a passenger-count change is applied to state (any
            // leg). Runs after setIn so each leg is measured against its updated passenger count.
            if (path[0] === 'transfers' && ['adults', 'children', 'kids'].includes(path[path.length - 1])) {
                previous = passengerCountRelated({ previous });
            }

            // update fields related to current time
            if (!notBookingDataChanges.includes(action.name)) {
                previous = currentTimeRelated(previous);
            }

            return touch(previous, path, action.value);

        case BOOKING_ACTION.TICK:
            return currentTimeRelated(previous);

        case BOOKING_ACTION.ADD_TRANSFER:
            const firstTransfer = previous.getIn(['transfers', 0]);
            const secondTransfer = {
                ...firstTransfer,
                addressFrom: firstTransfer.addressTo,
                addressTo: firstTransfer.addressFrom,
                flight: bookingInitialValue.getIn(['transfers', 0, 'flight']),
                typeId: BACK_TRANSFER[firstTransfer.typeId],
            };
            return previous.setIn(['transfers', 1], secondTransfer);

        case BOOKING_ACTION.REMOVE_TRANSFER:
            return bookingReducer(previous, {
                type: BOOKING_ACTION.UPDATE,
                name: 'transfers',
                value: [previous.getIn(['transfers', 0])],
            });

        case BOOKING_ACTION.TOUCH:
            return touch(previous, action.name.split('.'), action.value);

        case BOOKING_ACTION.SHOW_ERRORS:
            // value is a step's validationErrors object; touching its truthy leaves
            // makes the existing touched-gated error display show every invalid field.
            // Guard: touch(previous, [], null) would setIn(['touched'], true) and
            // destroy the touched map.
            if (!action.value || typeof action.value !== 'object') {
                return previous;
            }
            return touch(previous, [], action.value);

        case BOOKING_ACTION.UPDATE_USER:
            // Any identity change invalidates the loyalty redemption opt-in
            // (SoT §5.2 — redemption is never auto-applied).
            previous = previous.setIn(['redeemLoyaltyCredit'], false);
            if (!action.value) {
                previous = previous.setIn(['agencyId'], undefined);
                return previous.setIn(['user', 'id'], undefined);
            }
            // A real user is now attached to the booking, so any registration state left over
            // from the guest form is meaningless: CreateAccount is only rendered while logged
            // out (`{!user && createAccountComponent}` in BookingPaymentForm.jsx), and that
            // form force-sets `register: false` on submit for logged-in users. Without this,
            // a guest who ticked "Konto anlegen" and then logged in from the header would
            // keep `user.register === true` in state while the checkbox is hidden.
            previous = previous.setIn(['user', 'register'], false);
            [
                'firstName',
                'lastName',
                'companyName',
                'salutationId',
                'customerTypeId',
                'titleId',
                'mobile',
                'email',
                'ccemail',
                'address.zip',
                'address.place',
                'address.street',
                'address.phone',
                'address.countryId',
            ].forEach((key) => {
                const value = getIn(action.value, key);
                previous = bookingReducer(previous, { type: BOOKING_ACTION.UPDATE, name: `user.${key}`, value });
            });
            previous = bookingReducer(previous, { type: BOOKING_ACTION.UPDATE, name: 'user.password', value: '' });
            previous = bookingReducer(previous, {
                type: BOOKING_ACTION.UPDATE,
                name: 'user.passwordRepeat',
                value: '',
            });
            // update hidden fields
            previous = previous.setIn(['user', 'id'], action.value.id);
            previous = previous.setIn(['user', 'customerTypeId'], action.value.customerTypeId);
            previous = previous.setIn(['agencyId'], action.value.agencyId);
            return previous;

        default:
            console.error(`Unknown action type "${action.type}"`);
            return previous;
    }
};

const transfersRelated = ({ previous, path, action }) => {
    // not interested if the whole transfers or transfer is updated
    if (path.length <= 2) {
        return previous;
    }
    // inputs that drive the package list changed (any leg), so the customer must re-pick the category
    if (isScheduleProperty(path) && previous.getIn(path) !== action.value) {
        previous = clearServiceType(previous);
    }
    // update the second transfer if the first one is updated
    if (parseInt(path[1], 10) !== 0) {
        return previous;
    }

    const property = path[path.length - 1];

    // auto clean serviceTypeId when typeId is changed
    if (property === 'typeId' && previous.getIn(path) !== action.value) {
        previous = typeIdRelated({ previous, path, action });
    }
    // auto clean price when serviceTypeId is set
    if (property === 'serviceTypeId') {
        previous = serviceTypeIdRelated({ previous, path, action });
    }
    // copy extras
    if (path.includes('extras')) {
        previous = extrasRelated({ previous, path, action });
    }

    return previous;
};

const isScheduleProperty = (path) =>
    (path.length === 3 && ['arrivalTime', 'tripDate', 'tripTime'].includes(path[2])) ||
    (path.length === 4 && path[2] === 'flight' && ['date', 'time'].includes(path[3]));

// serviceType lives on the first leg; clearing its serviceTypeId also resets price and the second leg's copy
const clearServiceType = (previous) => {
    previous = bookingReducer(previous, {
        type: BOOKING_ACTION.UPDATE,
        name: 'transfers.0.serviceTypeId',
        value: undefined,
    });
    return bookingReducer(previous, { type: BOOKING_ACTION.UPDATE, name: 'transfers.0.serviceType', value: undefined });
};

const LEGROOM_MAX = 3;

const legPassengerCount = (transfer) =>
    transfer ? (transfer.adults || 0) + (transfer.children || 0) + (transfer.kids || 0) : 0;

const passengerCountRelated = ({ previous }) => {
    const transfers = previous.getIn(['transfers']);
    if (!transfers) {
        return previous;
    }

    // The passenger-count change is already applied to state; clamp EACH leg's legroom to
    // THAT leg's own passenger count (legroom is a per-leg paid seat). bookingClean repeats
    // this as a final defense at submit.
    transfers.forEach((transfer, index) => {
        const current = transfer.extras?.[SERVICES.EXTRA_LEGROOM];
        if (!current) {
            return;
        }
        const legMax = Math.min(legPassengerCount(transfer), LEGROOM_MAX);
        if (current > legMax) {
            previous = previous.setIn(['transfers', index, 'extras', SERVICES.EXTRA_LEGROOM], legMax);
        }
    });

    return previous;
};

const typeIdRelated = ({ previous, path, action }) => {
    previous = bookingReducer(previous, {
        type: BOOKING_ACTION.UPDATE,
        name: [...path.slice(0, -1), 'serviceTypeId'].join('.'),
        value: undefined,
    });
    previous = bookingReducer(previous, {
        type: BOOKING_ACTION.UPDATE,
        name: [...path.slice(0, -1), 'serviceType'].join('.'),
        value: undefined,
    });
    if (previous.getIn(['transfers', 1])) {
        if (action.value === TRANSFER.SPECIAL) {
            // remove second transfer
            previous = bookingReducer(previous, {
                type: BOOKING_ACTION.REMOVE_TRANSFER,
            });
        } else {
            // update second transfer
            const firstTransfer = previous.getIn(['transfers', 0]);
            const currentSecondTransfer = previous.getIn(['transfers', 1]);
            const secondTransfer = {
                ...currentSecondTransfer,
                addressFrom: firstTransfer.addressTo,
                addressTo: firstTransfer.addressFrom,
                typeId: BACK_TRANSFER[action.value],
            };
            previous = previous.setIn(['transfers', 1], secondTransfer);
        }
    }
    return previous;
};

const serviceTypeIdRelated = ({ previous, action }) => {
    previous = bookingReducer(previous, { type: BOOKING_ACTION.UPDATE, name: 'price', value: undefined });
    if (previous.getIn(['transfers', 1])) {
        // update serviceTypeId in the second transfer
        previous = bookingReducer(previous, {
            type: BOOKING_ACTION.UPDATE,
            name: 'transfers.1.serviceTypeId',
            value: action.value,
        });
    }
    return previous;
};

const extrasRelated = ({ previous, path, action }) => {
    const secondTransfer = previous.getIn(['transfers', 1]);
    if (!secondTransfer) {
        return previous;
    }

    // Copy the extra to the second leg. EXTRA_LEGROOM is a per-leg paid seat, so an asymmetric
    // round trip (e.g. leg1 = 3 pax, leg2 = 1 pax) must not carry leg1's quantity onto a smaller
    // second leg — clamp it to the second leg's own passenger count. bookingClean repeats this
    // as a final defense at submit.
    let value = action.value;
    if (path[path.length - 1] === SERVICES.EXTRA_LEGROOM) {
        value = Math.min(Number(value) || 0, legPassengerCount(secondTransfer), LEGROOM_MAX);
    }

    return bookingReducer(previous, {
        type: BOOKING_ACTION.UPDATE,
        name: `transfers.1.${path.slice(2).join('.')}`,
        value,
    });
};

const LATE_SERVICES = [SERVICES.LATE_TO_MUNICH, SERVICES.LATE_FROM_MUNICH, SERVICES.LATE];

// which express surcharge a transfer carries on the Munich clock right now, or null
const lateServiceFor = (transfer) => {
    if (!transfer.flight) {
        return null;
    }
    const isToAirport = [TRANSFER.TO_MUNICH, TRANSFER.TO_NUREMBERG].includes(transfer.typeId);
    const transferTime = isToAirport ? transfer.arrivalTime : transfer.flight.time;
    if (!isTransferLate(transferTime, transfer.flight.date)) {
        return null;
    }
    if (transfer.typeId === TRANSFER.TO_MUNICH) {
        return SERVICES.LATE_TO_MUNICH;
    }
    if (transfer.typeId === TRANSFER.FROM_MUNICH) {
        return SERVICES.LATE_FROM_MUNICH;
    }
    return SERVICES.LATE;
};

// Re-evaluate the express surcharge extras on the current (server-corrected) time.
// Pure, and returns `previous` itself when nothing flips: BookingPage dispatches this
// every minute and BookingPaymentForm reloads the quote on every transfers change, so a
// no-op must keep the reference.
const currentTimeRelated = (previous) => {
    const transfers = previous.getIn(['transfers']);

    if (!transfers || transfers.length === 0) {
        return previous;
    }

    const isSpecial = transfers[0].typeId === TRANSFER.SPECIAL;
    transfers.forEach((transfer, index) => {
        const wanted = isSpecial ? null : lateServiceFor(transfer);
        LATE_SERVICES.forEach((service) => {
            const desired = service === wanted ? 1 : 0;
            const current = Number(transfer.extras?.[service] || 0);
            if (current !== desired) {
                previous = previous.setIn(['transfers', index, 'extras', service], desired);
            }
        });
    });

    return previous;
};

export { bookingReducer, BOOKING_ACTION, currentTimeRelated };
